import { ConfigEntityDetail, EntityRelationship } from '../../types/entities';
import {
  AuditLogEndpointResponse,
  AuditLogRow,
  UserDetail,
} from '../../types/audit-log';
import { calculateEntityDiff } from '../calculate-entity-diff/calculate-entity-diff';

/**
 * Transforms audit log API response data into a structured format for display
 *
 * This function processes the raw API response and:
 * 1. Maps relationships between audit logs, users, and config entities
 * 2. Sorts config entities by version to determine fromEntity (lowest) and toEntity (highest)
 * 3. Concatenates user first and last names
 * 4. Handles different user types (regular users vs system users)
 *
 * @param apiData - The raw audit log API response containing data and included relationships
 * @returns Array of audit log row items ready for display in the AuditLogModal component
 */
export const transformAuditLogData = (
  apiData?: AuditLogEndpointResponse,
): AuditLogRow[] => {
  const { data, included } = apiData ?? {};
  if (!data || !included) return [];
  return data.map((item) => {
    // Extract user relationship data and find corresponding user details
    const userRelationship = item.relationships.user;
    const userData = included?.find(
      (inc): inc is UserDetail =>
        inc.id === (userRelationship.data as EntityRelationship).id &&
        inc.type === 'user',
    );

    // Extract config entity relationships and get all related entity IDs
    // Config entities are stored as arrays in the relationship data
    const configEntityRelationship = item.relationships.configEntity;
    const configEntityIds = Array.isArray(configEntityRelationship.data)
      ? configEntityRelationship.data.map((entity) => entity.id)
      : [];

    // Find all config entities from the included data that match the relationship IDs
    const configEntities =
      configEntityIds.length > 0
        ? included?.filter(
            (inc): inc is ConfigEntityDetail =>
              configEntityIds.includes(inc.id) && inc.type === 'config-entity',
          )
        : [];

    // Sort config entities by version number to determine fromEntity (lowest version)
    // and toEntity (highest version) for the audit log entry
    const sortedEntities = configEntities.sort((a, b) => {
      const versionA = a?.attributes?.entityMetadata?.version || 0;
      const versionB = b?.attributes?.entityMetadata?.version || 0;
      return versionA - versionB;
    });

    // Extract the lowest and highest version entities
    // For entity creation (only one entity), fromEntity should be null
    const fromEntity = sortedEntities.length > 1 ? sortedEntities[0] : null;
    const toEntity = sortedEntities[sortedEntities.length - 1];

    // Calculate differences between the two entity versions
    // For entity creation (fromEntity is null), treat everything in toEntity as new
    // For entity updates, compare fromEntity to toEntity
    const { differences, numberOfChanges } = toEntity
      ? calculateEntityDiff(fromEntity, toEntity)
      : { differences: [], numberOfChanges: 0 };

    // Build user display name based on user type
    // Regular users get first + last name, system users get "System"
    // If the userData is not found, user is set to an empty string
    let user = '';
    if (userData) {
      if (userData.attributes.userType === 'user') {
        if (userData.attributes.firstName && userData.attributes.lastName) {
          user =
            `${userData.attributes.firstName} ${userData.attributes.lastName}`.trim();
        } else if (userData.attributes.email) {
          user = userData.attributes.email;
        }
      } else {
        user = 'System';
      }
    }

    return {
      version: toEntity?.attributes?.entityMetadata?.version ?? 0,
      date: item.attributes.createdDate,
      user,
      status: item.attributes.status,
      actionType: item.attributes.actionType,
      numberOfChanges,
      contentDiff: differences,
      fromEntity: fromEntity || undefined,
      toEntity,
    };
  });
};
